import { createHandlerChainRunner } from "@insite/client-framework/HandlerCreator";
import { getOrder, GetOrderApiParameter } from "@insite/client-framework/Services/OrderService";
import { OrderModel } from "@insite/client-framework/Types/ApiModels";
import { CustomApiHandlerDiscreteParameter } from "@WhiteCap/Store/CustomHandlerCreator/CustomHandlerCreator";

type HandlerType = CustomApiHandlerDiscreteParameter<
    { orderNumber: string; expand?: ("orderLines" | "shipments" | "vmidetails")[] },
    GetOrderApiParameter,
    OrderModel
>;

export const DispatchBeginLoadOrder: HandlerType = props => {
    props.dispatch({
        type: "Custom/OrderDetails/BeginLoadOrder",
        orderNumber: props.parameter.orderNumber,
    });
};

export const PopulateApiParameter: HandlerType = props => {
    props.apiParameter = {
        orderNumber: props.parameter.orderNumber,
        expand: props.parameter.expand ?? ["orderLines", "shipments"],
    };
};

export const RequestDataFromApi: HandlerType = async props => {
    try {
        props.apiResult = await getOrder(props.apiParameter);
    } catch (error) {
        if ("status" in error && (error.status === 404 || error.status === 403)) {
            props.dispatch({
                type: "Custom/OrderDetails/FailedToLoadOrder",
                orderNumber: props.parameter.orderNumber,
                status: error.status,
            });
            return false;
        }
        throw error;
    }
};

export const FetchReturnOrdersIfPresent: HandlerType = async ({ apiResult, dispatch }) => {
    const orderNumber = apiResult.erpOrderNumber;
    const returnIdsProp = apiResult.properties?.returnOrderId;

    if (!returnIdsProp) {
        dispatch({
            type: "Custom/OrderDetails/ReturnOrdersLoaded",
            orderNumber,
            result: [],
        });
        return;
    }

    const returnOrderIds = returnIdsProp.split(";").map(id => id.trim());

    const returnOrdersData = await Promise.all(
        returnOrderIds.map(async id => {
            try {
                return await getOrder({
                    orderNumber: id,
                    expand: ["orderLines", "shipments"], // optional
                });
            } catch (err) {
                console.warn(`LoadOrder Failed to load return order ${id}:`, err);
                return null;
            }
        }),
    );

    const validReturnOrders = returnOrdersData.filter((returnOrder): returnOrder is OrderModel => !!returnOrder);

    dispatch({
        type: "Custom/OrderDetails/ReturnOrdersLoaded",
        orderNumber,
        result: validReturnOrders,
    });
};

export const DispatchCompleteLoadOrder: HandlerType = props => {
    props.dispatch({
        type: "Custom/OrderDetails/CompleteLoadOrder",
        model: props.apiResult,
    });
};

export const chain = [
    DispatchBeginLoadOrder,
    PopulateApiParameter,
    RequestDataFromApi,
    DispatchCompleteLoadOrder,
    FetchReturnOrdersIfPresent,
];

const loadOrderByOrderNumberCustom = createHandlerChainRunner(chain, "LoadOrderByOrderNumberCustom");
export default loadOrderByOrderNumberCustom;
